import { useEffect } from "react";

/**
 * Lightweight per-page SEO helper. Sets document.title, updates the
 * meta description + canonical link, and injects any provided JSON-LD.
 * Cleaned up on unmount so navigating away doesn't leak schema blocks.
 *
 * We do NOT depend on react-helmet-async — the static AEO block in
 * public/index.html is what LLM crawlers actually see. This helper is
 * purely for humans, GA4, and the browser tab.
 */
export function usePageSeo({ title, description, canonical, jsonLd }) {
  useEffect(() => {
    if (title) document.title = title;

    const upsertMeta = (name, content) => {
      if (!content) return null;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      return () => (prev ? el.setAttribute("content", prev) : el.remove());
    };

    const upsertLink = (rel, href) => {
      if (!href) return null;
      let el = document.querySelector(`link[rel="${rel}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("href");
      el.setAttribute("href", href);
      return () => (created ? el.remove() : el.setAttribute("href", prev || ""));
    };

    const cleanups = [
      upsertMeta("description", description),
      upsertLink("canonical", canonical),
    ].filter(Boolean);

    const scripts = [];
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      for (const block of blocks) {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.dataset.pageSeo = "1";
        script.textContent = typeof block === "string" ? block : JSON.stringify(block);
        document.head.appendChild(script);
        scripts.push(script);
      }
    }

    return () => {
      for (const fn of cleanups) fn();
      for (const s of scripts) s.remove();
    };
  }, [title, description, canonical, jsonLd]);
}
